/**
 * useInvestorLockDocs — state owner for the Investor Lock Documents section.
 *
 * Encapsulates the full state+effects bag (upload, drag/drop, preview,
 * set-current, delete, queue subscription) so `InvestorLockDocsCard` can
 * render in the pricing-v2 workspace.
 *
 * The returned object exposes the props the card needs plus a couple of
 * extras the workspace uses directly (deleteCandidate dialog, undo after
 * set-current).
 */

import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent, type RefObject } from 'react';
import { toast } from 'sonner';
import type { RealtimeSubscription } from '@supabase/supabase-js';
import {
  uploadLoanDocument,
  getInvestorLockDocuments,
  getDocumentDownloadUrl,
  setInvestorLockCurrent,
  deleteInvestorLockDocument,
  subscribeToQueueUpdates
} from '@/services/loanDocumentService';
import type { ApplicationDocument } from '@/types/loan-documents';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useInvestorLockDocs', 'pricing-v2');

export interface UseInvestorLockDocsResult {
  docs: ApplicationDocument[];
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  isDragging: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onDownload: (doc: ApplicationDocument) => void;
  onSetCurrent: (doc: ApplicationDocument) => void;
  onDelete: (doc: ApplicationDocument) => void;
  /** Pending delete target — workspace owns the AlertDialog around it. */
  deleteCandidate: ApplicationDocument | null;
  clearDeleteCandidate: () => void;
  confirmDelete: () => Promise<void>;
}

export function useInvestorLockDocs(encompassLoanId: string): UseInvestorLockDocsResult {
  const [docs, setDocs] = useState<ApplicationDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<ApplicationDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionsRef = useRef<Map<string, RealtimeSubscription>>(new Map());

  const subscribeToDocument = useCallback((doc: ApplicationDocument) => {
    if (!doc.queueId || subscriptionsRef.current.has(doc.queueId)) {
      return;
    }
    const subscription = subscribeToQueueUpdates(doc.queueId, (status) => {
      const nextQueueStatus = status.status as ApplicationDocument['queueStatus'];
      const nextQueueError = status.status === 'failed' ? (status.error ?? null) : null;

      setDocs((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? {
                ...d,
                queueStatus: nextQueueStatus,
                queueProgress: typeof status.progress === 'number' ? status.progress : (d.queueProgress ?? null),
                queueError: nextQueueError
              }
            : d
        )
      );

      if (status.status === 'completed') {
        subscription.unsubscribe();
        subscriptionsRef.current.delete(doc.queueId!);
        toast.success('Document processed successfully');
      } else if (status.status === 'failed') {
        subscription.unsubscribe();
        subscriptionsRef.current.delete(doc.queueId!);
        toast.error(status.error || 'Document processing failed');
      }
    });

    subscriptionsRef.current.set(doc.queueId, subscription);
  }, []);

  const loadDocs = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetched = await getInvestorLockDocuments(encompassLoanId);
      setDocs(fetched);
      fetched.forEach((doc) => {
        if (doc.queueId && doc.queueStatus && doc.queueStatus !== 'completed' && doc.queueStatus !== 'failed') {
          subscribeToDocument(doc);
        }
      });
    } catch (error) {
      log.error('Failed to load investor lock documents', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [encompassLoanId, subscribeToDocument]);

  useEffect(() => {
    if (encompassLoanId) {
      void loadDocs();
    }
    const currentSubscriptions = subscriptionsRef.current;
    return () => {
      currentSubscriptions.forEach((sub) => sub.unsubscribe());
      currentSubscriptions.clear();
    };
  }, [encompassLoanId, loadDocs]);

  const uploadFile = useCallback(
    async (file: File) => {
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are supported');
        return;
      }
      try {
        setIsUploading(true);
        setUploadProgress(0);
        await uploadLoanDocument({
          file,
          encompassLoanId,
          documentType: 'investor_lock',
          title: file.name,
          onProgress: setUploadProgress
        });
        await loadDocs();
        toast.success('Document uploaded and set as current lock confirmation');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload document';
        log.error('Investor lock upload failed', { encompassLoanId }, error as Error);
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [encompassLoanId, loadDocs]
  );

  const onFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      await uploadFile(file);
      event.target.value = '';
    },
    [uploadFile]
  );

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) {
        await uploadFile(file);
      }
    },
    [uploadFile]
  );

  const onDownload = useCallback(async (doc: ApplicationDocument) => {
    try {
      if (!doc.storageUrl) {
        throw new Error('Document storage URL not found');
      }
      const url = await getDocumentDownloadUrl(doc.storageUrl);
      window.open(url, '_blank');
    } catch (error) {
      log.error('Download failed', {}, error as Error);
      toast.error('Failed to download document');
    }
  }, []);

  const onSetCurrent = useCallback(
    async (doc: ApplicationDocument) => {
      if (doc.isCurrentVersion) {
        return;
      }
      const previousCurrentId = docs.find((d) => d.isCurrentVersion)?.id ?? null;
      try {
        const result = await setInvestorLockCurrent(doc.id);
        if (!result.success) {
          toast.error(
            result.reason === 'concurrent_change_detected'
              ? 'Another change happened. Refreshing…'
              : result.message || 'Failed to set as current'
          );
          await loadDocs();
          return;
        }
        await loadDocs();
        toast(
          `Lock v${doc.version ?? '?'} is now current`,
          previousCurrentId
            ? {
                action: {
                  label: 'Undo',
                  onClick: async () => {
                    await setInvestorLockCurrent(previousCurrentId);
                    await loadDocs();
                  }
                }
              }
            : undefined
        );
      } catch (error) {
        log.error('Set-current failed', {}, error as Error);
        toast.error('Failed to set as current');
      }
    },
    [docs, loadDocs]
  );

  const onDelete = useCallback((doc: ApplicationDocument) => {
    setDeleteCandidate(doc);
  }, []);

  const clearDeleteCandidate = useCallback(() => setDeleteCandidate(null), []);

  const confirmDelete = useCallback(async () => {
    const doc = deleteCandidate;
    if (!doc) {
      return;
    }
    try {
      await deleteInvestorLockDocument(doc.id);
      await loadDocs();
      toast.success(
        doc.isCurrentVersion ? 'Current lock deleted. Previous version auto-promoted.' : 'Lock version deleted'
      );
    } catch (error) {
      log.error('Delete failed', {}, error as Error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteCandidate(null);
    }
  }, [deleteCandidate, loadDocs]);

  return {
    docs,
    isLoading,
    isUploading,
    uploadProgress,
    isDragging,
    fileInputRef,
    onFileSelect,
    onDragOver,
    onDragLeave,
    onDrop,
    onDownload,
    onSetCurrent,
    onDelete,
    deleteCandidate,
    clearDeleteCandidate,
    confirmDelete
  };
}
